import UIProvider from 'providers/UI';
import AuthProvider from 'providers/Auth';
import { ConfigProvider, App as AntApp } from 'antd';
import { useTranslation } from 'react-i18next';
import { Provider } from 'react-redux';
import { persistor, store } from 'store';
import { PersistGate } from 'redux-persist/integration/react';
import { useEffect } from 'react';

import Routes from './routes';
import { ConfirmModal, DoctorApp, RejectModal } from 'components/shared';
import PrivacyPolicy from 'pages/PrivacyPolicy';
import { validateMessages } from 'utils/validation';
import { getFormLang } from 'utils/helpers';
import { Loading } from 'components/ui';
import ReportPreview from 'pages/ReportDetails/components/Preview';
import useIsMobile from 'hooks/useIsMobile';
import AudioCall from 'components/shared/AudioCall';

const App = () => {
  // Translations
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();

  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    const lang = search.get('lang');

    if (lang) {
      search.delete('lang');

      if (lang === 'en' || lang === 'de') {
        i18n.changeLanguage(lang);
      }

      const newSearch = search.toString();

      const baseUrl = window.location.origin + window.location.pathname;

      const newUrl = baseUrl + (newSearch ? `?${newSearch}` : '');

      window.history.replaceState(null, '', newUrl);
    }
  }, [i18n]);

  if (window.location.pathname === '/privacy-policy') {
    return <PrivacyPolicy />;
  }

  if (isMobile) {
    return <DoctorApp />;
  }

  return (
    <div className="App">
      <Provider store={store}>
        <PersistGate loading={<Loading />} persistor={persistor}>
          <ConfigProvider
            form={{ validateMessages: validateMessages(t) }}
            locale={getFormLang(i18n.language)}
            theme={{
              components: {
                Layout: {
                  headerHeight: 48,
                  headerPadding: '0 24px',
                  siderBg: '#F5F7FF',
                  lightSiderBg: '#F5F7FF'
                },
                Button: {
                  borderRadius: 2,
                  borderRadiusLG: 2,
                  borderRadiusSM: 2,
                  borderRadiusXS: 2,
                  borderRadiusOuter: 2
                },
                Input: {
                  borderRadius: 2,
                  borderRadiusLG: 2,
                  borderRadiusSM: 2,
                  borderRadiusXS: 2,
                  borderRadiusOuter: 2
                },
                Select: {
                  borderRadius: 2,
                  borderRadiusLG: 2,
                  borderRadiusSM: 2,
                  borderRadiusXS: 2,
                  borderRadiusOuter: 2
                },
                DatePicker: {
                  borderRadius: 2,
                  borderRadiusLG: 2,
                  borderRadiusSM: 2,
                  borderRadiusXS: 2,
                  borderRadiusOuter: 2
                },
                Segmented: {
                  itemSelectedBg: '#2552F3',
                  itemSelectedColor: '#ffffff',
                  itemHoverBg: 'transparent',
                  borderRadius: 2,
                  borderRadiusLG: 2,
                  borderRadiusSM: 2,
                  borderRadiusXS: 2,
                  borderRadiusOuter: 2
                }
              }
            }}
          >
            <AntApp>
              <AuthProvider>
                <UIProvider>
                  <Routes />
                  <ConfirmModal />
                  <RejectModal />
                  <ReportPreview />
                  <AudioCall />
                </UIProvider>
              </AuthProvider>
            </AntApp>
          </ConfigProvider>
        </PersistGate>
      </Provider>
    </div>
  );
};

export default App;
